import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { ShieldCheck, Mail, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
let autoLoginCodeSent = null;

// Cambia estos archivos por tus imágenes y colócalos dentro de la carpeta public/.
const BRAND_IMAGE_SRC = '/mi-imagen-marca.svg';
const ACCESS_IMAGE_SRC = '/mi-imagen-acceso.svg';
const NeonCard = ({ children }) => (
  <div style={{ position:'relative', borderRadius:'16px' }}>
    <div style={{ position:'absolute', inset:'-1px', background:'linear-gradient(135deg,#58d6dd 0%,#6558e8 58%,#ff623f 100%)', backgroundSize:'300% 300%', animation:'borderRotate 4s linear infinite', borderRadius:'16px', zIndex:0 }}/>
    <div style={{ position:'relative', zIndex:1, background:'rgba(18,25,48,0.90)', borderRadius:'15px', backdropFilter:'blur(28px)', WebkitBackdropFilter:'blur(28px)' }}>
      {children}
    </div>
  </div>
);

const Landing = ({ prefilledCode = '', onOtpHandled }) => {
  const navigate    = useNavigate();
  const { setAuth } = useAuth();
  const [step, setStep]             = useState('code');
  const [accessCode, setAccessCode] = useState('');
  const [otp, setOtp]               = useState(['','','','','','']);
  const [loading, setLoading]       = useState(false);
  const [emailHint, setEmailHint]   = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [focused, setFocused]       = useState(null);
  const otpRefs = useRef([]);
  useEffect(() => {
  if (!prefilledCode) return;
  const code = prefilledCode.trim();
  setAccessCode(code);

  // Ya se lanzó el auto-login para este código: no repetir
  if (autoLoginCodeSent === code) return;
  autoLoginCodeSent = code;

  // Quita ?code= de la URL: un F5 ya no reenvía otro OTP
  window.history.replaceState({}, '', window.location.pathname);

  const autoLogin = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/login-by-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ access_code: code }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Código inválido');
      if (data.access_token) {
        setAuth(data.access_token, data.user);
        redirectByRole(data.user.role);
        return;
      }
      if (data.requires_otp) {
        setEmailHint(data.email_hint);
        setStep('otp');
        setResendCooldown(60);
        toast.success(data.message);
      }
    } catch (err) {
      autoLoginCodeSent = null;   // permite reintentar si falló
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };
  autoLogin();
}, [prefilledCode]);
  const redirectByRole = (role) => {

    if (role==='superadmin') navigate('/superadmin');
    else if (role==='admin') navigate('/admin');
    else navigate('/dashboard');
  };

  const handleLoginByCode = async (e) => {
  e.preventDefault(); setLoading(true);
  try {
    const res = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/login-by-code`, {
      method:'POST',
      headers:{'Content-Type':'application/json'},
      body:JSON.stringify({access_code:accessCode.trim()})
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail||'Código inválido');
    if (data.access_token) {
      setAuth(data.access_token,data.user);
      redirectByRole(data.user.role);
      return;
    }
    if (data.requires_otp) {
      setEmailHint(data.email_hint);
      setStep('otp');
      setResendCooldown(60);
      toast.success(data.message);
    }
  } catch(err) { toast.error(err.message); } finally { setLoading(false); }
};



  const handleVerifyOtp = async (otpOverride) => {
    const otpStr = otpOverride??otp.join('');
    if (otpStr.length<6) return; setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/verify-otp`, {
        method:'POST',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify({access_code:accessCode.trim(),otp:otpStr})
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail||'Código incorrecto');
      setAuth(data.access_token,data.user); redirectByRole(data.user.role);
    } catch(err) { toast.error(err.message); setOtp(['','','','','','']); otpRefs.current[0]?.focus(); }
    finally { setLoading(false); }
  };

  const handleResend = async () => {
    if (resendCooldown>0) return; setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/login-by-code`, {
        method:'POST',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify({access_code:accessCode.trim()})
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      toast.success('Nuevo código enviado'); setResendCooldown(60); setOtp(['','','','','','']); otpRefs.current[0]?.focus();
    } catch(err) { toast.error(err.message); } finally { setLoading(false); }
  };

  const handleOtpChange = (idx, val) => {
    if (!/^\d*$/.test(val)) return;
    const next=[...otp]; next[idx]=val.slice(-1); setOtp(next);
    if (val&&idx<5) otpRefs.current[idx+1]?.focus();
    if (next.join('').length===6) setTimeout(()=>handleVerifyOtp(next.join('')),80);
  };
  const handleOtpKeyDown = (idx,e) => { if (e.key==='Backspace'&&!otp[idx]&&idx>0) otpRefs.current[idx-1]?.focus(); };
  const handleOtpPaste = (e) => {
    const p=e.clipboardData.getData('text').replaceAll(/\D/g,'').slice(0,6);
    if (p.length===6) { setOtp(p.split('')); otpRefs.current[5]?.focus(); setTimeout(()=>handleVerifyOtp(p),80); }
  };

  const inp = (id) => ({
    width:'100%', padding:'13px 16px', boxSizing:'border-box',
    background: focused===id ? 'rgba(98,216,223,0.07)' : 'rgba(10,16,34,0.62)',
    border: focused===id ? '1px solid #62d8df' : '1px solid rgba(98,216,223,0.16)',
    borderRadius:'10px', color:'#f5f3ff', fontSize:'14px',
    fontFamily:"'Inter', 'Segoe UI', sans-serif", outline:'none', transition:'all 0.25s',
    boxShadow: focused===id ? '0 0 20px rgba(98,216,223,0.16)' : 'none',
  });
  const lbl = { display:'block', fontSize:'9px', fontWeight:700, letterSpacing:'0.22em', color:'#c9c0ff', marginBottom:'7px', fontFamily:"'Inter', 'Segoe UI', sans-serif" };
  const btnS = (disabled) => ({
    width:'100%', padding:'14px', border:'none', borderRadius:'10px',
    background: disabled ? 'rgba(98,216,223,0.07)' : 'linear-gradient(135deg,#58d6dd,#6558e8)',
    color: disabled ? 'rgba(98,216,223,0.28)' : '#17213b', fontSize:'12px', fontWeight:700,
    letterSpacing:'0.18em', cursor: disabled?'not-allowed':'pointer', transition:'all 0.3s',
    boxShadow: disabled ? 'none' : '0 0 32px rgba(98,216,223,0.3)',
    fontFamily:"'Inter', 'Segoe UI', sans-serif",
  });

  return (
    <div style={{
      minHeight:'100vh',
      display:'flex', alignItems:'center', justifyContent:'center',
      position:'relative', overflow:'hidden',
      fontFamily:"'Inter', 'Segoe UI', sans-serif",
      backgroundImage:"url('/bg-login.png')",
      backgroundSize:'cover',
      backgroundPosition:'center center',
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        @keyframes borderRotate { 0%{background-position:0% 50%} 50%{background-position:100% 50%} 100%{background-position:0% 50%} }
        @keyframes floatIcon    { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }
        @keyframes spinSlow     { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @keyframes scanLine     { 0%{top:-2px} 100%{top:100%} }
        @keyframes flicker      { 0%,19%,21%,23%,25%,54%,56%,100%{opacity:1} 20%,22%,24%,55%{opacity:0.4} }
        input::placeholder { color:rgba(98,216,223,0.18); }
        .login-btn:hover:not(:disabled) { box-shadow:0 10px 30px rgba(101,88,232,0.32) !important; transform:translateY(-2px); }
        @media (max-width: 700px) {
          .login-panel-shell { position:relative !important; left:auto !important; right:auto !important; top:auto !important; transform:none !important; width:min(400px, calc(100% - 28px)) !important; margin:88px auto 24px !important; }
        }
      `}</style>

      <div style={{ position:'fixed', inset:0, zIndex:0, background:'rgba(12,18,36,0.08)', backdropFilter:'blur(0.5px)' }}/>
      <div style={{ position:'fixed', left:0, right:0, height:'2px', zIndex:1, background:'linear-gradient(90deg,transparent,rgba(98,216,223,0.35),transparent)', animation:'scanLine 18s linear infinite' }}/>
      <div style={{ position:'fixed', top:0, left:0, right:0, height:'2px', background:'linear-gradient(90deg,transparent,rgba(98,216,223,0.6),rgba(101,88,232,0.4),transparent)', zIndex:2 }}/>
      <div style={{ position:'fixed', bottom:0, left:0, right:0, height:'2px', background:'linear-gradient(90deg,transparent,rgba(101,88,232,0.5),rgba(98,216,223,0.6),transparent)', zIndex:2 }}/>
      <div style={{ position:'fixed', top:0, bottom:0, left:0, width:'2px', background:'linear-gradient(180deg,rgba(98,216,223,0.4),rgba(101,88,232,0.25),transparent)', zIndex:2 }}/>
      <div style={{ position:'fixed', top:0, bottom:0, right:0, width:'2px', background:'linear-gradient(180deg,rgba(101,88,232,0.4),rgba(98,216,223,0.25),transparent)', zIndex:2 }}/>

      <div style={{ position:'fixed', top:'24px', left:'28px', display:'flex', alignItems:'center', gap:'10px', zIndex:10 }}>
        <div style={{ width:'52px', height:'52px', flexShrink:0, background:'linear-gradient(135deg,#58d6dd,#6558e8)', borderRadius:'9px', display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 0 24px rgba(98,216,223,0.4)' }}>
          <img src={BRAND_IMAGE_SRC} alt="Marca" style={{ width:'100%', height:'100%', objectFit:'contain', padding:'4px', boxSizing:'border-box', borderRadius:'9px' }} />
        </div>
        <div>
          <div style={{ fontSize:'11px', fontWeight:700, color:'#2d12b5', letterSpacing:'0.28em', animation:'flicker 9s infinite', textShadow:'0 0 10px rgba(119, 46, 255, 0.6)' }}>FORMAICIONIA</div>
          <div style={{ fontSize:'8px', color:'rgba(59, 10, 158, 0.82)', letterSpacing:'0.35em' }}>ACADEMY</div>
        </div>
      </div>

      <div className="login-panel-shell" style={{ position:'absolute', left:'clamp(48px, 9vw, 180px)', top:'50%', transform:'translateY(-50%)', width:'min(380px, calc(100% - 32px))', maxWidth:'380px', boxSizing:'border-box', zIndex:10 }}>
        <NeonCard>
          <div style={{ padding:'36px 32px' }}>

            {step==='code' && (
              <>
                <div style={{ textAlign:'center', marginBottom:'30px' }}>
                  <div style={{ width:'82px', height:'82px', margin:'0 auto 18px', background:'rgba(98,216,223,0.05)', border:'1px solid rgba(98,216,223,0.28)', borderRadius:'14px', display:'flex', alignItems:'center', justifyContent:'center', animation:'floatIcon 3.5s ease-in-out infinite', boxShadow:'0 0 30px rgba(98,216,223,0.18)' }}>
                    <img src={ACCESS_IMAGE_SRC} alt="Icono de acceso" style={{ width:'62px', height:'62px', objectFit:'contain' }} />
                  </div>
                  <h1 style={{ fontSize:'24px', fontWeight:700, color:'#f5f3ff', margin:'0 0 6px', letterSpacing:'0.14em', textShadow:'0 0 24px rgba(98,216,223,0.25)' }}>ACCEDER</h1>
                  <p style={{ fontSize:'9px', color:'rgba(98,216,223,0.38)', letterSpacing:'0.22em', margin:0 }}>
                    INGRESA TU CÓDIGO DE ACCESO
                  </p>
                </div>

                <form onSubmit={handleLoginByCode} style={{ display:'flex', flexDirection:'column', gap:'18px' }}>
                  <div>
                    <label style={lbl}>Código de Acceso</label>
                    <input type="text" value={accessCode} onChange={e=>setAccessCode(e.target.value.toUpperCase().replaceAll(/[^A-Z0-9-]/g,''))}
                      style={{...inp('code'),textAlign:'center',fontSize:'22px',letterSpacing:'0.45em'}}
                      onFocus={()=>setFocused('code')} onBlur={()=>setFocused(null)}
                      placeholder="XXXX-XXXX" maxLength={9} autoComplete="off" autoFocus required />
                  </div>
                  <button type="submit" className="login-btn" style={btnS(loading||accessCode.length<4)} disabled={loading||accessCode.length<4}>
                    {loading?'⟳  VERIFICANDO...':'▶  ENTRAR AL SISTEMA'}
                  </button>
                </form>

                <div style={{ display:'flex', alignItems:'center', gap:'10px', marginTop:'22px' }}>
                  <div style={{ flex:1, height:'1px', background:'rgba(98,216,223,0.07)' }}/>
                  <span style={{ fontSize:'7px', color:'rgba(98,216,223,0.22)', letterSpacing:'0.25em' }}>NEXTECH v2.0</span>
                  <div style={{ flex:1, height:'1px', background:'rgba(98,216,223,0.07)' }}/>
                </div>
              </>
            )}

            {step==='otp' && (
              <>
                <div style={{ textAlign:'center', marginBottom:'22px' }}>
                  <div style={{ width:'62px', height:'62px', margin:'0 auto 18px', border:'2px solid rgba(98,216,223,0.32)', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', background:'rgba(98,216,223,0.04)', animation:'spinSlow 9s linear infinite', boxShadow:'0 0 28px rgba(98,216,223,0.18)' }}>
                    <ShieldCheck size={27} style={{ color:'#62d8df', filter:'drop-shadow(0 0 8px rgba(98,216,223,0.8))' }} />
                  </div>
                  <h1 style={{ fontSize:'20px', fontWeight:700, color:'#f5f3ff', margin:'0 0 6px', letterSpacing:'0.14em' }}>VERIFICACIÓN</h1>
                  <p style={{ fontSize:'9px', color:'rgba(98,216,223,0.38)', margin:0 }}>
                    Código enviado a <strong style={{ color:'#62d8df', textShadow:'0 0 8px rgba(98,216,223,0.5)' }}>{emailHint}</strong>
                  </p>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:'10px', padding:'10px 14px', background:'rgba(98,216,223,0.04)', border:'1px solid rgba(98,216,223,0.14)', borderRadius:'10px', marginBottom:'20px' }}>
                  <Mail size={12} style={{ color:'#62d8df', flexShrink:0 }} />
                  <p style={{ fontSize:'9px', color:'rgba(98,216,223,0.45)', margin:0 }}>Revisa tu bandeja. Caduca en <strong style={{ color:'#62d8df' }}>10 min</strong>.</p>
                </div>
                <div style={{ display:'flex', gap:'7px', justifyContent:'center', marginBottom:'20px' }} onPaste={handleOtpPaste}>
                  {otp.map((digit,idx) => (
                    <input key={idx} ref={el=>otpRefs.current[idx]=el} type="text" inputMode="numeric" maxLength={1} value={digit}
                      onChange={e=>handleOtpChange(idx,e.target.value)} onKeyDown={e=>handleOtpKeyDown(idx,e)}
                      style={{ width:'44px', height:'52px', textAlign:'center', fontSize:'20px', fontWeight:700, background:digit?'rgba(98,216,223,0.09)':'rgba(10,16,34,0.62)', border:digit?'2px solid #62d8df':'1px solid rgba(98,216,223,0.14)', borderRadius:'10px', color:digit?'#62d8df':'rgba(98,216,223,0.18)', outline:'none', boxShadow:digit?'0 0 18px rgba(98,216,223,0.28)':'none', transition:'all 0.2s', fontFamily:"'Inter', 'Segoe UI', sans-serif", opacity:loading?0.5:1 }}
                      autoFocus={idx===0}
                    />
                  ))}
                </div>
                <button className="login-btn" style={btnS(loading||otp.join('').length<6)} onClick={()=>handleVerifyOtp()} disabled={loading||otp.join('').length<6}>
                  {loading?'⟳  VERIFICANDO...':'▶  CONFIRMAR ACCESO'}
                </button>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:'16px' }}>
                  <button onClick={()=>{setStep('code');setOtp(['','','','','','']);}} style={{ display:'flex', alignItems:'center', gap:'5px', background:'none', border:'none', cursor:'pointer', color:'rgba(98,216,223,0.32)', fontSize:'9px', fontFamily:"'Inter', 'Segoe UI', sans-serif", letterSpacing:'0.14em' }}>
                    <ArrowLeft size={11}/> VOLVER
                  </button>
                  <button onClick={handleResend} disabled={resendCooldown>0||loading} style={{ background:'none', border:'none', cursor:resendCooldown>0?'not-allowed':'pointer', color:resendCooldown>0?'rgba(98,216,223,0.22)':'#62d8df', fontSize:'9px', fontFamily:"'Inter', 'Segoe UI', sans-serif", letterSpacing:'0.14em', fontWeight:700 }}>
                    {resendCooldown>0?`REENVIAR (${resendCooldown}s)`:'REENVIAR CÓDIGO'}
                  </button>
                </div>
              </>
            )}

          </div>
        </NeonCard>
      </div>
    </div>
  );
};

export default Landing;